import { useRef, type Ref } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import type { FilterDict } from '../../components/list-controls/types';
import '../../styles/list-controls.css';
import {
  DEMO_DOCUMENTS,
  DEMO_FACETS,
  facetLabel,
  filterDocuments,
  nonEmptyFilterKeys,
  valueLabel,
  type DemoDocument
} from './landingDocumentsDemoData';
import { LandingMockPointer } from './LandingMockPointer';
import { useDocumentsFilterLoop, type DemoTarget, type PopoverFacet } from './useDocumentsFilterLoop';

const LAYOUT_SPRING = { type: 'spring' as const, stiffness: 440, damping: 19, mass: 0.82 };
const CHIP_SPRING = { type: 'spring' as const, stiffness: 380, damping: 26, mass: 0.9 };
const CHIP_EXIT = { opacity: 0, scale: 0.48, filter: 'blur(10px)' };
const CHIP_EXIT_TRANSITION = { duration: 0.34, ease: [0.4, 0, 0.2, 1] as const };
const GALLERY_ENTER = { opacity: 0, scale: 0.9, filter: 'blur(8px)' };
const GALLERY_ANIMATE = { opacity: 1, scale: 1, filter: 'blur(0px)' };
const GALLERY_EXIT = { opacity: 0, scale: 0.78, filter: 'blur(12px)' };
const GALLERY_EXIT_TRANSITION = { duration: 0.34, ease: [0.32, 0, 0.67, 0] as const };

function DocGalleryCard({ doc }: { doc: DemoDocument }) {
  return (
    <div className="doc-gallery-card" tabIndex={-1}>
      <div className="doc-gallery-card-type">{doc.typeLabel}</div>
      <div className="doc-gallery-card-title">{doc.title}</div>
      <p className="doc-gallery-card-preview">{doc.preview}</p>
      <div className="doc-gallery-card-footer">
        <span className="doc-gallery-card-chip" data-lifecycle={doc.lifecycle}>
          {doc.lifecycle === 'draft' ? 'Draft' : 'Published'}
        </span>
        <span className="doc-gallery-card-time">2h</span>
      </div>
    </div>
  );
}

function DemoSearch() {
  return (
    <div className="pn-listctl-search" aria-hidden="true">
      <span className="pn-listctl-search-icon" aria-hidden="true">
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10Zm6 2-3.2-3.2"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <input
        className="pn-listctl-search-input"
        type="search"
        placeholder="Search documents…"
        readOnly
        tabIndex={-1}
      />
    </div>
  );
}

function DemoFilterPopover({
  open,
  facet,
  filters,
  demoHighlight,
  facetOwnerRef,
  facetTypeRef,
  filterBackRef,
  valueOwnerMeRef,
  valueTypePrdRef
}: {
  open: boolean;
  facet: PopoverFacet;
  filters: FilterDict;
  demoHighlight: DemoTarget | null;
  facetOwnerRef: Ref<HTMLButtonElement>;
  facetTypeRef: Ref<HTMLButtonElement>;
  filterBackRef: Ref<HTMLButtonElement>;
  valueOwnerMeRef: Ref<HTMLLabelElement>;
  valueTypePrdRef: Ref<HTMLLabelElement>;
}) {
  if (!open) return null;

  const ownerSelected = (filters.owner ?? []).includes('me');
  const typeSelected = (filters.type ?? []).includes('prd');

  if (facet === 'owner') {
    const ownerFacet = DEMO_FACETS.find((f) => f.key === 'owner')!;
    return (
      <div className="lp-doc-demo-popover" aria-hidden="true">
        <div className="pn-listctl-filter-pane" data-testid="lp-mock-filter-pane-owner">
          <button
            ref={filterBackRef}
            type="button"
            className="pn-listctl-filter-back"
            data-demo-active={demoHighlight === 'filterBack' ? 'true' : undefined}
            data-testid="lp-mock-filter-back"
            tabIndex={-1}
          >
            <span aria-hidden="true">←</span> {ownerFacet.label}
          </button>
          <label
            ref={valueOwnerMeRef}
            className="pn-listctl-filter-value-row"
            data-demo-active={demoHighlight === 'valueOwnerMe' ? 'true' : undefined}
            data-testid="lp-mock-filter-value-owner-me"
          >
            <input type="checkbox" readOnly checked={ownerSelected} tabIndex={-1} />
            <span>Me</span>
          </label>
        </div>
      </div>
    );
  }

  if (facet === 'type') {
    const typeFacet = DEMO_FACETS.find((f) => f.key === 'type')!;
    return (
      <div className="lp-doc-demo-popover" aria-hidden="true">
        <div className="pn-listctl-filter-pane" data-testid="lp-mock-filter-pane-type">
          <button type="button" className="pn-listctl-filter-back" tabIndex={-1}>
            <span aria-hidden="true">←</span> {typeFacet.label}
          </button>
          {typeFacet.options.map((opt) => (
            <label
              key={opt.id}
              ref={opt.id === 'prd' ? valueTypePrdRef : undefined}
              className="pn-listctl-filter-value-row"
              data-demo-active={
                opt.id === 'prd' && demoHighlight === 'valueTypePrd' ? 'true' : undefined
              }
              data-testid={`lp-mock-filter-value-type-${opt.id}`}
            >
              <input
                type="checkbox"
                readOnly
                checked={opt.id === 'prd' ? typeSelected : false}
                tabIndex={-1}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (facet === 'lifecycle') {
    const lifecycleFacet = DEMO_FACETS.find((f) => f.key === 'lifecycle')!;
    return (
      <div className="lp-doc-demo-popover" aria-hidden="true">
        <div className="pn-listctl-filter-pane" data-testid="lp-mock-filter-pane-lifecycle">
          <button type="button" className="pn-listctl-filter-back" tabIndex={-1}>
            <span aria-hidden="true">←</span> {lifecycleFacet.label}
          </button>
          {lifecycleFacet.options.map((opt) => (
            <label
              key={opt.id}
              className="pn-listctl-filter-value-row"
              data-testid={`lp-mock-filter-value-lifecycle-${opt.id}`}
            >
              <input
                type="checkbox"
                readOnly
                checked={(filters.lifecycle ?? []).includes(opt.id)}
                tabIndex={-1}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="lp-doc-demo-popover" aria-hidden="true">
      <div className="pn-listctl-filter-pane" data-testid="lp-mock-filter-pane-root">
        <div className="pn-listctl-filter-pane-head">Add a filter</div>
        {DEMO_FACETS.map((f) => {
          const count = (filters[f.key] ?? []).length;
          const ref =
            f.key === 'owner' ? facetOwnerRef : f.key === 'type' ? facetTypeRef : undefined;
          const highlight =
            f.key === 'owner'
              ? demoHighlight === 'facetOwner'
              : f.key === 'type'
                ? demoHighlight === 'facetType'
                : false;
          return (
            <button
              key={f.key}
              ref={ref}
              type="button"
              className="pn-listctl-filter-facet-row"
              data-demo-active={highlight ? 'true' : undefined}
              data-testid={`lp-mock-filter-facet-${f.key}`}
              tabIndex={-1}
            >
              <span className="pn-listctl-filter-facet-row-label">{f.label}</span>
              {count > 0 ? (
                <span className="pn-listctl-filter-facet-row-count">{count}</span>
              ) : null}
              <span className="pn-listctl-filter-facet-row-chev" aria-hidden="true">
                ›
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DemoActiveChips({
  filters,
  chipTypeXRef,
  chipOwnerXRef,
  demoHighlight
}: {
  filters: FilterDict;
  chipTypeXRef: Ref<HTMLButtonElement>;
  chipOwnerXRef: Ref<HTMLButtonElement>;
  demoHighlight: DemoTarget | null;
}) {
  const keys = nonEmptyFilterKeys(filters);
  if (keys.length === 0) return null;

  return (
    <AnimatePresence mode="popLayout">
      {keys.map((key) => {
        const values = filters[key] ?? [];
        const text = values.map((v) => valueLabel(key, v)).join(', ');
        const xRef = key === 'type' ? chipTypeXRef : key === 'owner' ? chipOwnerXRef : undefined;
        const xHighlight =
          key === 'type'
            ? demoHighlight === 'chipTypeX'
            : key === 'owner'
              ? demoHighlight === 'chipOwnerX'
              : false;
        return (
          <motion.span
            key={key}
            className="pn-listctl-active-chip"
            data-testid={`lp-mock-active-chip-${key}`}
            layout="position"
            style={{ transformOrigin: 'center center' }}
            initial={{ opacity: 0, scale: 0.85, filter: 'blur(4px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ ...CHIP_EXIT, transition: CHIP_EXIT_TRANSITION }}
            transition={{
              layout: LAYOUT_SPRING,
              default: CHIP_SPRING
            }}
          >
            {facetLabel(key)}: {text}
            <button
              ref={xRef}
              type="button"
              className="pn-listctl-active-chip-x"
              aria-hidden="true"
              tabIndex={-1}
              data-demo-active={xHighlight ? 'true' : undefined}
              data-testid={`lp-mock-active-chip-clear-${key}`}
            >
              ×
            </button>
          </motion.span>
        );
      })}
    </AnimatePresence>
  );
}

function DocumentsDemoBody({
  filters,
  popoverOpen,
  popoverFacet,
  popoverFilters,
  showCursor,
  pointerClicking,
  cursor,
  demoHighlight,
  filterTriggerRef,
  facetOwnerRef,
  facetTypeRef,
  filterBackRef,
  valueOwnerMeRef,
  valueTypePrdRef,
  chipTypeXRef,
  chipOwnerXRef,
  stageRef
}: {
  filters: FilterDict;
  popoverOpen: boolean;
  popoverFacet: PopoverFacet;
  popoverFilters: FilterDict;
  showCursor: boolean;
  pointerClicking: boolean;
  cursor: { x: number; y: number };
  demoHighlight: DemoTarget | null;
  filterTriggerRef: Ref<HTMLButtonElement>;
  facetOwnerRef: Ref<HTMLButtonElement>;
  facetTypeRef: Ref<HTMLButtonElement>;
  filterBackRef: Ref<HTMLButtonElement>;
  valueOwnerMeRef: Ref<HTMLLabelElement>;
  valueTypePrdRef: Ref<HTMLLabelElement>;
  chipTypeXRef: Ref<HTMLButtonElement>;
  chipOwnerXRef: Ref<HTMLButtonElement>;
  stageRef: Ref<HTMLDivElement>;
}) {
  const visibleDocs = filterDocuments(filters);
  const chipsVisible = !popoverOpen;

  return (
    <div ref={stageRef} className="lp-doc-demo-stage-inner">
      <div className="pn-listctl lp-doc-demo-listctl" data-testid="lp-mock-documents-controlbar">
        <DemoSearch />
        <div className="lp-doc-demo-listctl-row2">
          <button
            ref={filterTriggerRef}
            type="button"
            className="pn-btn pn-btn-sm"
            data-demo-active={demoHighlight === 'filterTrigger' ? 'true' : undefined}
            data-testid="lp-mock-filter-trigger"
            tabIndex={-1}
          >
            + Filter
          </button>
          <div className="lp-doc-demo-chips-slot" aria-hidden={!chipsVisible}>
            {chipsVisible ? (
              <DemoActiveChips
                filters={filters}
                chipTypeXRef={chipTypeXRef}
                chipOwnerXRef={chipOwnerXRef}
                demoHighlight={demoHighlight}
              />
            ) : null}
          </div>
        </div>
      </div>

      <DemoFilterPopover
        open={popoverOpen}
        facet={popoverFacet}
        filters={popoverFilters}
        demoHighlight={demoHighlight}
        facetOwnerRef={facetOwnerRef}
        facetTypeRef={facetTypeRef}
        filterBackRef={filterBackRef}
        valueOwnerMeRef={valueOwnerMeRef}
        valueTypePrdRef={valueTypePrdRef}
      />

      <div className="doc-gallery lp-doc-demo-gallery">
        <div className="doc-gallery-grid lp-doc-demo-gallery-grid">
          <AnimatePresence mode="popLayout">
            {visibleDocs.map((doc) => (
              <motion.div
                key={doc.id}
                className="lp-doc-demo-gallery-cell"
                layout
                style={{ transformOrigin: 'center center' }}
                initial={GALLERY_ENTER}
                animate={GALLERY_ANIMATE}
                exit={{ ...GALLERY_EXIT, transition: GALLERY_EXIT_TRANSITION }}
                transition={{
                  layout: LAYOUT_SPRING,
                  default: CHIP_SPRING
                }}
              >
                <DocGalleryCard doc={doc} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <LandingMockPointer
        visible={showCursor}
        x={cursor.x}
        y={cursor.y}
        clicking={pointerClicking}
      />
    </div>
  );
}

export function LandingIdeDocumentsStatic() {
  return (
    <div className="lp-doc-demo-stage">
      <div className="lp-doc-demo-stage-inner">
        <div className="pn-listctl lp-doc-demo-listctl" data-testid="lp-mock-documents-controlbar">
          <DemoSearch />
          <div className="lp-doc-demo-listctl-row2">
            <button
              type="button"
              className="pn-btn pn-btn-sm"
              data-testid="lp-mock-filter-trigger"
              tabIndex={-1}
            >
              + Filter
            </button>
            <div className="lp-doc-demo-chips-slot" aria-hidden="true" />
          </div>
        </div>
        <div className="doc-gallery lp-doc-demo-gallery">
          <div className="doc-gallery-grid lp-doc-demo-gallery-grid">
            {DEMO_DOCUMENTS.map((doc) => (
              <div key={doc.id} className="lp-doc-demo-gallery-cell">
                <DocGalleryCard doc={doc} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingIdeDocumentsAnimated() {
  const stageWrapRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stageWrapRef, { amount: 0.2, once: false });
  const loop = useDocumentsFilterLoop(inView);

  return (
    <div ref={stageWrapRef} className="lp-doc-demo-stage">
      <DocumentsDemoBody {...loop} />
    </div>
  );
}

export function LandingIdeDocumentsDemo() {
  const reducedMotion = useReducedMotion();
  if (reducedMotion) {
    return <LandingIdeDocumentsStatic />;
  }
  return <LandingIdeDocumentsAnimated />;
}
